import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-property-detail-skeleton',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="min-h-screen bg-gray-50 animate-pulse">
      <!-- Top Navigation Bar Skeleton -->
      <div class="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-4 flex items-center justify-between">
          <div class="h-6 sm:h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg w-1/2 sm:w-1/3"></div>
          <div class="flex items-center gap-2 sm:gap-3">
            <div class="w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full"></div>
            <div class="h-9 sm:h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg w-16 sm:w-20"></div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <!-- Image Gallery - Left Column -->
          <div class="lg:col-span-2 space-y-4">
            <!-- Main Image -->
            <div class="bg-white rounded-xl overflow-hidden shadow-lg relative">
              <div class="bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 aspect-video relative overflow-hidden">
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
              </div>
              <!-- Featured Badge Skeleton -->
              <div class="absolute top-4 right-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full h-8 w-24"></div>
              <!-- Image Counter Skeleton -->
              <div class="absolute bottom-4 left-4 bg-gray-800 bg-opacity-40 rounded-full h-7 w-16"></div>
            </div>

            <!-- Image Navigation -->
            <div class="flex gap-2">
              <div class="flex-1 h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg border border-gray-200"></div>
              <div class="flex-1 h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg border border-gray-200"></div>
            </div>

            <!-- Image Thumbnails -->
            <div class="flex gap-2 overflow-x-auto pb-2">
              <div class="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
              <div class="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
              <div class="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
              <div class="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
              <div class="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
            </div>

            <!-- Property Info Cards -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-6">
              <div class="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-200 to-blue-300 rounded-lg"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
                    <div class="h-5 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-full"></div>
                  </div>
                </div>
              </div>
              <div class="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-200 to-emerald-300 rounded-lg"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
                    <div class="h-5 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-full"></div>
                  </div>
                </div>
              </div>
              <div class="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-200 to-purple-300 rounded-lg"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
                    <div class="h-5 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-full"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description Section -->
            <div class="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100 mt-6 space-y-6">
              <!-- Description Header -->
              <div class="space-y-3">
                <div class="h-7 bg-gradient-to-r from-gray-300 to-gray-400 rounded-lg w-48"></div>
                <div class="space-y-2.5">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-11/12"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-4/5"></div>
                </div>
              </div>

              <!-- Features Section -->
              <div class="pt-6 border-t border-gray-200 space-y-3">
                <div class="h-6 bg-gradient-to-r from-gray-300 to-gray-400 rounded-lg w-40"></div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="flex items-center gap-3">
                    <div class="w-5 h-5 bg-gradient-to-br from-emerald-200 to-emerald-300 rounded"></div>
                    <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-1"></div>
                  </div>
                  <div class="flex items-center gap-3">
                    <div class="w-5 h-5 bg-gradient-to-br from-emerald-200 to-emerald-300 rounded"></div>
                    <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-1"></div>
                  </div>
                  <div class="flex items-center gap-3">
                    <div class="w-5 h-5 bg-gradient-to-br from-emerald-200 to-emerald-300 rounded"></div>
                    <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-1"></div>
                  </div>
                  <div class="flex items-center gap-3">
                    <div class="w-5 h-5 bg-gradient-to-br from-emerald-200 to-emerald-300 rounded"></div>
                    <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-1"></div>
                  </div>
                </div>
              </div>

              <!-- Location Section -->
              <div class="pt-6 border-t border-gray-200 space-y-3">
                <div class="h-6 bg-gradient-to-r from-gray-300 to-gray-400 rounded-lg w-32"></div>
                <div class="space-y-2">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar - Right Column -->
          <div class="lg:col-span-1">
            <!-- Price Card -->
            <div class="bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-gray-100 sticky top-24 space-y-6">
              <!-- Price Section -->
              <div class="space-y-3">
                <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
                <div class="h-10 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg w-full"></div>
                <div class="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
              </div>

              <!-- Quick Details -->
              <div class="space-y-4 pt-6 border-t border-gray-200">
                <div class="flex justify-between items-center">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-16"></div>
                </div>
                <div class="flex justify-between items-center">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-16"></div>
                </div>
                <div class="flex justify-between items-center">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-16"></div>
                </div>
                <div class="flex justify-between items-center">
                  <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                  <div class="h-4 bg-gradient-to-r from-gray-300 to-gray-400 rounded w-16"></div>
                </div>
              </div>

              <!-- Agent Info -->
              <div class="pt-6 border-t border-gray-200 space-y-3">
                <div class="flex items-center gap-3">
                  <div class="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                    <div class="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="space-y-3 pt-4">
                <div class="w-full h-12 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg"></div>
                <div class="w-full h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg border border-gray-200"></div>
                <div class="w-full h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg border border-gray-200"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes shimmer {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
    
    .animate-shimmer {
      animation: shimmer 2s infinite;
    }
  `]
})
export class PropertyDetailSkeletonComponent {}
